import { computed, inject, Injectable, signal } from '@angular/core';
import {
  adminUserApi,
  adminUserDtos,
  dsApi,
  dsDtos,
  userApi,
  userDtos,
} from '@vectronic/openApi-admin';
import { SelectableDeviceIds, SelectedId } from '@vectronic/util';
import { catchError, combineLatest, map, Observable, of, shareReplay, startWith } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UserService {
  private collarApi = inject(dsApi.Collars);
  private trapApi = inject(dsApi.Traps);

  readonly allCollarIds$: Observable<number[]> = this.collarApi.getIds({}).pipe(
    catchError(() => of([])),
    shareReplay(1),
  );

  readonly allTrapIds$: Observable<number[]> = this.trapApi.getIds({}).pipe(
    catchError(() => of([])),
    shareReplay(1),
  );

  // Keep them separate, plus provide a ready-made union if you ever need it.
  readonly allSelectableDeviceIds$: Observable<SelectableDeviceIds> = combineLatest({
    collars: this.allCollarIds$.pipe(startWith([] as number[])),
    traps: this.allTrapIds$.pipe(startWith([] as number[])),
  }).pipe(
    map(({ collars, traps }) => ({
      collars: collars ?? [],
      traps: traps ?? [],
    })),
    shareReplay(1),
  );

  private adminUserApi = inject(adminUserApi.Users);
  private userAdminApi = inject(userApi.Account);

  readonly admins = signal<adminUserDtos.User[]>([]);
  readonly users = signal<userDtos.UserAccountLight[]>([]);
  readonly commandCreator = computed<(dsDtos.CommandCreator & { label: string })[]>(() => {
    const admins = this.admins();
    const users = this.users();
    return [
      ...admins.map((admin) => ({ isAdmin: true, creatorId: admin.id, label: admin.initials })),
      ...users.map((user) => ({ isAdmin: false, creatorId: user.id, label: user.email })),
      { isAdmin: false, creatorId: -1, label: 'GPS PLUS X' },
    ];
  });

  readonly commandCreatorFullName = computed<(dsDtos.CommandCreator & { label: string })[]>(() => {
    const admins = this.admins();
    const users = this.users();
    return [
      ...admins.map((admin) => ({
        isAdmin: true,
        creatorId: admin.id,
        label:
          [admin.firstName, admin.lastName].filter(Boolean).join(' ') ||
          admin.initials ||
          admin.email ||
          '',
      })),
      ...users.map((user) => ({
        isAdmin: false,
        creatorId: user.id,
        label: user.name || user.email || '',
      })),
      { isAdmin: false, creatorId: -1, label: 'GPS PLUS X' },
    ];
  });

  constructor() {
    this.fetchUser();

    this.allSelectableDeviceIds$.subscribe();
  }

  fetchUser() {
    this.userAdminApi.getLightUsers().subscribe((data) => {
      this.users.set(data);
    });

    this.adminUserApi.getUsers().subscribe((users) => {
      this.admins.set(users);
    });
  }

  extractIds(selectedIds: SelectedId[]): number[] {
    return selectedIds.reduce<number[]>((acc, item) => {
      if (item.checked) acc.push(item.id);
      return acc;
    }, []);
  }
}
